import { bitcoinjs, getEcpairFromPrivateKey } from '@optimex-xyz/btc-script'
import {
  AssetChainPresignRequest,
  AssetChainPresignResponse,
  GetEphemeralAssetChainResponse,
  GetEphemeralL2ConfigResponse,
  IWalletSigner,
  L2ConfigSignRequest,
  L2ConfigSignResponse,
} from '@optimex-xyz/trade-wallet-sdk'
import { Keypair, Transaction } from '@solana/web3.js'

import { Wallet } from 'ethers'

import { SUPPORTED_NETWORK } from './constants'
import { EphemeralInfo, getEphemeral } from './getEphemeral'
import { ensureHexPrefix, removeHexPrefix } from './trade'

export class EphemeralWalletSigner implements IWalletSigner {
  private ephemeralInfo: EphemeralInfo

  constructor(hexSignature: string) {
    this.ephemeralInfo = getEphemeral(hexSignature)
  }

  async getEphemeralAssetChain(): Promise<GetEphemeralAssetChainResponse> {
    return {
      btcPublicKey: this.ephemeralInfo.publicKey('assetChain', 'BTC'),
      evmPublicKey: this.ephemeralInfo.publicKey('assetChain', 'EVM'),
      solanaPublicKey: this.ephemeralInfo.publicKey('assetChain', 'SOLANA'),
      evmAddress: this.ephemeralInfo.assetChain.evm.address,
    }
  }

  async getEphemeralL2Config(): Promise<GetEphemeralL2ConfigResponse> {
    return {
      address: this.ephemeralInfo.l2.address,
    }
  }

  async assetChainSign(body: AssetChainPresignRequest): Promise<AssetChainPresignResponse> {
    switch (body.chainType) {
      case 'btc': {
        const result = await Promise.all(
          body.data.map(async (item) => {
            return this.btcSign(this.ephemeralInfo.assetChain.btc, item, body.network)
          })
        )

        return { signatures: result }
      }
      case 'evm': {
        const result = await Promise.all(
          body.data.map(async (item) => {
            return this.evmSign(this.ephemeralInfo.assetChain.evm, item)
          })
        )

        return { signatures: result }
      }
      case 'solana': {
        const result = await Promise.all(
          body.data.map(async (item) => {
            return this.solanaSign(this.ephemeralInfo.assetChain.sol, item)
          })
        )

        return { signatures: result }
      }

      default: {
        throw new Error('Invalid chain type')
      }
    }
  }

  async l2ConfigSign(body: L2ConfigSignRequest): Promise<L2ConfigSignResponse> {
    const result = await this.evmSign(this.ephemeralInfo.l2, body.data)

    return {
      signature: result,
      evmAddress: this.ephemeralInfo.assetChain.evm.address,
    }
  }

  l2PrivateKey(): string {
    return this.ephemeralInfo.l2.privateKey
  }

  private btcSign(ephemeralKP: ReturnType<typeof getEcpairFromPrivateKey>, data: string, network: string) {
    const psbt = bitcoinjs.Psbt.fromHex(removeHexPrefix(data), {
      network: network === SUPPORTED_NETWORK.BTC_TESTNET ? bitcoinjs.networks.testnet : bitcoinjs.networks.bitcoin,
    })

    psbt.signInput(0, ephemeralKP)

    return ensureHexPrefix(psbt.toHex())
  }

  private solanaSign(ephemeralSolanaWallet: Keypair, data: string) {
    const transaction = Transaction.from(Buffer.from(removeHexPrefix(data), 'hex'))
    transaction.sign(ephemeralSolanaWallet)
    return Buffer.from(
      transaction.serialize({
        requireAllSignatures: false,
        verifySignatures: false,
      })
    ).toString('hex')
  }

  private async evmSign(ephemeralWallet: Wallet, data: string) {
    const signature = ephemeralWallet.signingKey.sign(data).serialized

    return ensureHexPrefix(signature)
  }

  public getEphemeralKey(type: 'l2' | 'assetChain') {
    return this.ephemeralInfo.privateKey(type)
  }
}
